import { Box } from '@mui/material';
import { SharedDocumentsIcon } from 'assets/img/icons';
import { DocumentsIcon } from 'assets/img/menu';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import Filter from 'Components/Filter/Filter';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import ScrollableTableContainer from 'Components/ScrollableTableContainer';
import { EmptyStateCard, Tabs, ThemeButton } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import DocumentSkeleton from 'FeatureModules/Document/Components/DocumentSkeleton';
import DocumentTable from 'FeatureModules/Document/Components/Tables/DocumentTable';
import { EDocumentsTableColumnKeys } from 'FeatureModules/Document/utils/documentHeaderColumns';
import { EDocumentLibraryTabs } from 'FeatureModules/Document/types/document.types';
import useSiteDocuments from 'FeatureModules/Site/hooks/useSiteDocuments';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';

const DOCUMENT_TABS: (ITab<EDocumentLibraryTabs> & {
  onlyForBuyer?: boolean;
})[] = [
  {
    key: EDocumentLibraryTabs.INTERNAL,
    label: 'Internal',
    startIcon: <DocumentsIcon height={12} />,
  },
  {
    key: EDocumentLibraryTabs.SHARED,
    label: 'Shared with me',
    startIcon: <SharedDocumentsIcon height={12} />,
    onlyForBuyer: true,
  },
];

const SiteDocuments: FC = () => {
  const navigate = useNavigate();
  const {
    documents,
    loading,
    error,
    searchTerm,
    filters,
    selectedTab,
    hasNextPage,
    handlePageEndReached,
    handleFilterChange,
    handleFilterReset,
    handleSearchTermChange,
    handleCreateDocument,
    handleNewRequestClick,
    handleTabChange,
  } = useSiteDocuments();
  const { plan } = useCompanyPlanContext();

  const isFreemium = plan === ECustomerPlan.FREEMIUM;

  if (error) {
    return <ErrorState />;
  }
  if (loading) {
    return <Loader />;
  }

  if (!documents.length && !searchTerm && !filters.some(({ value }) => !!value)) {
    return (
      <PageContainer>
        <PageTitle title="Site documents" goBackLabel="All sites" goBackUrl="/sites" />

        {isFreemium ? (
          <Box mb={3} />
        ) : (
          <Box mt={1} mb={2}>
            <Tabs tabs={DOCUMENT_TABS} selectedTab={selectedTab} onChange={handleTabChange} />
          </Box>
        )}

        {selectedTab === EDocumentLibraryTabs.INTERNAL ? (
          <EmptyStateCard
            title="Add documents to your sites to keep track of important information like proof of no deforestation"
            variant={EItemTypes.DOCUMENT}
            buttonText="Add new document"
            background={EBackgroundTypes.DETAILED_DOCUMENTS}
            buttonIcon={<Icon name="document-request-add" color="white" />}
            size={EAvailableSizes.LARGE}
            data-testid-button="add-new-document-btn"
            onCreateItemClick={handleCreateDocument}
          />
        ) : (
          <EmptyStateCard
            title="Request documents to your sites to keep track of important information like proof of no deforestation"
            variant={EItemTypes.DOCUMENT}
            buttonText="Request new document"
            buttonIcon={<Icon name="document-request-pending" color="white" />}
            background={EBackgroundTypes.DETAILED_DOCUMENTS}
            size={EAvailableSizes.LARGE}
            onCreateItemClick={handleNewRequestClick}
          />
        )}
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle title="Site documents" goBackLabel="All sites" goBackUrl="/sites" />

      {isFreemium ? (
        <Box mb={3} />
      ) : (
        <Box mt={1} mb={2}>
          <Tabs tabs={DOCUMENT_TABS} selectedTab={selectedTab} onChange={handleTabChange} />
        </Box>
      )}

      <Filter
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
      />

      <Box position="relative">
        <ActionContainer>
          <SearchToolbar
            searchTerm={searchTerm}
            placeholder="Search document"
            className="search-input"
            setSearchTerm={handleSearchTermChange}
            actions={
              <Box display="flex" ml={1}>
                {selectedTab === EDocumentLibraryTabs.INTERNAL ? (
                  <ThemeButton
                    data-testid="add-new-document-btn"
                    startIcon={<Icon name="document" />}
                    onClick={handleCreateDocument}
                  >
                    Add new document
                  </ThemeButton>
                ) : (
                  <ThemeButton
                    data-testid="request-new-document-btn"
                    startIcon={<Icon name="document-request-pending" />}
                    onClick={handleNewRequestClick}
                  >
                    Request new document
                  </ThemeButton>
                )}
              </Box>
            }
          />
        </ActionContainer>

        {!!documents.length && (
          <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
            <ScrollableTableContainer>
              <DocumentTable
                multiselect={false}
                disableSelection
                columns={[
                  EDocumentsTableColumnKeys.TITLE,
                  EDocumentsTableColumnKeys.OWNER,
                  EDocumentsTableColumnKeys.USAGE,
                  EDocumentsTableColumnKeys.ISSUE_DATE,
                  EDocumentsTableColumnKeys.EXPIRY_DATE,
                ]}
                documents={documents}
                onDocumentClick={({ id }) =>
                  navigate(
                    `/documents/${
                      selectedTab === EDocumentLibraryTabs.INTERNAL ? 'internal' : 'shared'
                    }/${id}`
                  )
                }
              />
            </ScrollableTableContainer>
          </InfiniteScrollWrapper>
        )}
      </Box>

      {!documents?.length && (
        <EmptySearchResults
          message="No search results found"
          skeletonComponent={<DocumentSkeleton title={searchTerm} />}
        />
      )}
    </PageContainer>
  );
};

export default SiteDocuments;
